import { msalInstance } from './msalConfig';
import { getIdToken } from './token';

/**
 * Calling our own API with the Entra ID token, and the two answers this layer
 * can give when that token turns out to be dead: mint a new one and send the
 * request again, or — when a token minted seconds ago is refused too — stop
 * pretending there is a session and go to the login page.
 *
 * Both fetch helpers (`api.ts`, `api/client.ts`) go through here so neither
 * has to know about either. Before this, a 401 was simply an `ApiError` like
 * any other: only `_auth`'s loader treated it as a session problem, and only
 * on the load of an authenticated route. A 401 raised by anything else — a
 * react-query refetch, the pending-count poll on its timer, a photo upload
 * from the kiosk — surfaced as a red toast on a page that would keep failing,
 * which is a dead end an operator cannot act on and cannot leave.
 */
export async function authorizedFetch(
  url: string,
  init?: RequestInit,
): Promise<Response> {
  const res = await sendWithToken(url, init, false);
  if (res.status !== 401) {
    return res;
  }

  // 401 is the API telling us the bearer is not good, which `getIdToken`
  // cannot find out on its own: MSAL hands back whatever is in its cache, and
  // the cache can be wrong in ways only the server sees — a clock skewed past
  // the token's `exp`, a signing key rotated, an API restarted onto a
  // configuration the token no longer matches.
  //
  // Replaying is safe. `JwtAuthGuard` is a global guard, so a 401 is decided
  // before any handler runs: nothing was uploaded, generated or deleted, and
  // the request is as unsent as it was a moment ago. (The body must be
  // replayable, which every caller's is — a string, a `Blob` or a `FormData`,
  // never a stream.)
  const replay = await sendWithToken(url, init, true);
  if (replay.status !== 401) {
    return replay;
  }

  // Refused twice, the second time holding a token Entra issued moments ago.
  // Nothing about this is a freshness problem any more, so there is no third
  // attempt and no trip through Entra to make: it would come back with the
  // same token and be refused the same way.
  endSession();
  return replay;
}

async function sendWithToken(
  url: string,
  init: RequestInit | undefined,
  forceRefresh: boolean,
): Promise<Response> {
  const token = await getIdToken(msalInstance, { forceRefresh });
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return fetch(url, { ...init, headers });
}

/** So that a page failing on five requests at once navigates once. */
let ending = false;

/**
 * Give up on the session: forget the active account and go to `/login`.
 *
 * The account goes first so that the fresh document does not simply resume as
 * whoever just got refused — `_auth`'s guard reads exactly that, and the
 * bootstrap only ever elects an account a completed sign-in designates.
 *
 * A whole-page navigation, where the rest of this app is careful to avoid one.
 * `token.ts` refuses to redirect on a dropped connection precisely because it
 * costs an operator their uncommitted crop or their filled-in form; the
 * difference is that a request survives a dropped connection and none survives
 * this. Every unsaved thing on the page is now unsavable, so keeping the page
 * only keeps the operator from finding that out.
 *
 * `replace`, not `assign`: the page behind us answers 401 to everything, and
 * the back button should not offer to go and prove it.
 */
export function endSession(): void {
  if (ending) {
    return;
  }
  ending = true;
  msalInstance.setActiveAccount(null);
  window.location.replace('/login');
}

/** @internal Test seam: `ending` is module state and outlives a test. */
export function __resetSessionEndForTests(): void {
  ending = false;
}
